import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastType } from 'react-toastify';

import { BLACKBAUD_CLIENT_ID, type BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import { Button, CardV2 as Card, Step, toast } from '@feathr/components';
import { useAccount, useStore } from '@feathr/extender/state';

import * as styles from './RaisersEdgeConnectBlackbaudCard.css';

interface IProps {
  integration?: BlackbaudRaisersEdgeIntegration;
}

function RaisersEdgeConnectBlackbaudCard({ integration }: IProps): JSX.Element {
  const account = useAccount();
  const { t } = useTranslation();
  const { BlackbaudRaisersEdgeIntegrations } = useStore();
  const [confirmed, setConfirmed] = useState(
    !!account.getSetting('confirmed_bbrenxt_marketplace_sync'),
  );
  const [isConnecting, setIsConnecting] = useState(false);

  async function handleClickConnect(): Promise<void> {
    setIsConnecting(true);
    try {
      // Check for existing integration or create one
      let existingIntegration = integration;

      if (!existingIntegration) {
        const redirectUri = `${globalThis.location.origin}/oauth/blackbaud`;
        const newIntegration = BlackbaudRaisersEdgeIntegrations.create();
        existingIntegration = await BlackbaudRaisersEdgeIntegrations.add(newIntegration, {
          validate: false,
          redirectUri,
        });

        if (existingIntegration?.isErrored) {
          toast(t('Failed to create Blackbaud integration. Please try again.'), {
            type: ToastType.ERROR,
          });
          return;
        }

        BlackbaudRaisersEdgeIntegrations.clearApiCache();
      }

      // Construct OAuth URL with all required params
      const loginUrl = new URL('https://app.blackbaud.com/oauth/authorize');
      loginUrl.searchParams.set('client_id', BLACKBAUD_CLIENT_ID);
      loginUrl.searchParams.set('response_type', 'code');
      loginUrl.searchParams.set('redirect_uri', `${globalThis.location.origin}/oauth/blackbaud`);

      // Add state parameter with encoded return path back to this page
      const returnPath = globalThis.location.pathname;
      loginUrl.searchParams.set('state', btoa(returnPath));

      globalThis.location.assign(loginUrl.toString());
    } catch (error) {
      toast(t('There was an error connecting to Blackbaud. Please try again.'), {
        type: ToastType.ERROR,
      });
    } finally {
      setIsConnecting(false);
    }
  }

  function handleClickConfirmMarketplaceSync(): void {
    account.setSetting('confirmed_bbrenxt_marketplace_sync', true);
    setConfirmed(true);
    try {
      account.patchDirty();
    } catch (error) {
      toast(t('There was an error saving your confirmation:\n{{- error}}', { error }), {
        type: ToastType.ERROR,
      });
    }
  }

  return (
    <>
      <div className={styles.header}>
        <h4>{t('2 steps to get started')}</h4>
        <p>{t('Start syncing all your data by completing these tasks.')}</p>
      </div>
      <Card
        key={'add-feathr-application'}
        theme={!confirmed ? 'selected' : 'default'}
        width={'full'}
      >
        <Card.Header
          description={
            <>
              {t("Add the Feathr application to your Raiser's Edge NXT account. ")}
              <Button
                href={`https://app.blackbaud.com/marketplace/applications/${BLACKBAUD_CLIENT_ID}?action=getstarted`}
                name={'add-application-link'}
                suffix={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
                target={'_blank'}
                type={'link'}
              >
                {t('Application marketplace')}
              </Button>
            </>
          }
          padding={'relaxed'}
          prefix={<Step status={confirmed ? 'finished' : 'processing'} stepNumber={1} />}
          title={t('Add Feathr application')}
        >
          {!confirmed && (
            <Button onClick={handleClickConfirmMarketplaceSync}>{t('Confirm and continue')}</Button>
          )}
        </Card.Header>
      </Card>
      <Card key={'connect-blackbaud'} theme={confirmed ? 'selected' : 'disabled'} width={'full'}>
        <Card.Header
          description={t(
            "Use your Blackbaud admin credentials to connect Raiser's Edge to Feathr.",
          )}
          padding={'relaxed'}
          prefix={<Step status={confirmed ? 'processing' : 'waiting'} stepNumber={2} />}
          title={t('Connect Blackbaud account')}
        >
          <Button disabled={!confirmed} isLoading={isConnecting} onClick={handleClickConnect}>
            {t('Connect Blackbaud')}
          </Button>
        </Card.Header>
      </Card>
    </>
  );
}

export default observer(RaisersEdgeConnectBlackbaudCard);
